import { useState, useCallback,useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Modal, Box, Typography, Card, Grid, Stack, Switch, Button } from '@mui/material';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import axiosInstance, { getErrorMessage } from 'utils/axios';
import { FormProvider, RHFSelect, RHFTextField, RHFUploadAvatar } from 'components/hook-form';
import { LoadingButton } from '@mui/lab';
import Label from 'components/Label';
import CRUD from 'utils/Crud';
import ReactCrop from 'react-image-crop';
import { fData } from 'utils/formatNumber';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 600,
    bgcolor: 'background.paper',
    border: '2px solid whitesmoke',
    boxShadow: 24,
    p: 4,
};


const NewUserSchema = Yup.object().shape({
    first_name: Yup.string().required('Name is required'),
    last_name: Yup.string().required('Name is required'),
    phone: Yup.string().required('Phone is required'),
    email: Yup.string().required('Email is required').email(),
    gender: Yup.string().required('Field is required'),
    referral_id: Yup.string().required('Field is required'),
});

EditUserModal.propTypes = {
    open: PropTypes.bool,
    handleClose: PropTypes.func,
    handleComplete: PropTypes.func,
    user: PropTypes.object,
}

export default function EditUserModal({handleClose, user, handleComplete }) {

    const { enqueueSnackbar } = useSnackbar();
    const [state, setState] = useState({ imageRef: null, croppedImageUrl: null, });
    const [cropImage, setCropImage] = useState(null);
    const [crop, setCrop] = useState({
        unit: '%', // Can be 'px' or '%'
        x: 25,
        y: 25,
        width: 80,
        height: 80,
    });

    const defaultValues = useMemo(
        () => ({
            first_name: user?.first_name || '',
            last_name: user?.last_name || '',
            email: user?.email || '',
            phone: user?.phone || '',
            status: user?.status,
            gender: user?.gender || '',
            referral_id: user?.referral_id || '',
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [user]
    );

    const methods = useForm({
        resolver: yupResolver(NewUserSchema),
        defaultValues,
    });

    const {
        reset,
        watch,
        setValue,
        handleSubmit,
        formState: { isSubmitting },
    } = methods;

    const values = watch();

    useEffect(() => {
        reset(defaultValues);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const onSubmit = async () => {
        
        try {

            const formData = new FormData()
            formData.append('first_name', values.first_name);
            formData.append('last_name', values.last_name);
            formData.append('gender', values.gender);
            formData.append('phone', values.phone);
            formData.append('referral_id', values.referral_id);
            formData.append('user_id', user.user_id);
            formData.append('email', values.email);
            formData.append('userfile', cropImage)

            values.user_id = user.user_id;
            await axiosInstance.post(CRUD.user.update,formData);

            reset();
            enqueueSnackbar('Update success!');
            handleComplete();
        } catch (error) {
            enqueueSnackbar(getErrorMessage(error));
        }
    };

    const handleDrop = useCallback(
        (acceptedFiles) => {
        const file = acceptedFiles[0];

        if (file) {
        setValue(
            'avatarUrl',
            Object.assign(file, {
                preview: URL.createObjectURL(file),
            })
        );
        }
    }, [setValue]);

    return <Dialog open onClose={handleClose}>
            <DialogTitle>Edit profile</DialogTitle>
            
                <DialogContent>
                    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                        <Card sx={{ p: 3, mt: 4 }}>
                            <Box sx={{ mb: 5 }}>
                                    {values.avatarUrl == null &&
                                        <RHFUploadAvatar
                                            name="avatarUrl"
                                            accept="image/png,image/jpg,image/jpeg"
                                            maxSize={3145728}
                                            onDrop={handleDrop}
                                            helperText={
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                mt: 2,
                                                mx: 'auto',
                                                display: 'block',
                                                textAlign: 'center',
                                                color: 'text.secondary',
                                                }}
                                            >
                                                Allowed *.jpeg, *.jpg, *.png, *.gif
                                                <br /> max size of {fData(3145728)}
                                                <p>Leave empty if you dont want to update current icon</p>
                                            </Typography>
                                            }
                                        />}
                                    {values.avatarUrl != null && (
                                        <Box sx={{display:"flex",justifyContent:"center"}}>
                                            <ReactCrop 
                                                crop={crop} 
                                                onComplete={onCropComplete}  
                                                onChange={c => setCrop(c)}>
                                                <img 
                                                    onLoad ={(e)=>{
                                                        getCroppedImg(e.currentTarget,crop);
                                                        setState({...state,imageRef:e.currentTarget});
                                                    }} 
                                                    src={values.avatarUrl.preview} 
                                                    style={{width:180}} alt="" />
                                            </ReactCrop>
                                        </Box>
                                        
                                    ) }
                                    </Box>

                            <Box  >
                                <Grid container spacing={2}>
                                    <Grid item md={6}>
                                        <RHFTextField name="first_name" label="First Name" sx={{ mb: 3 }} />
                                    </Grid>
                                    <Grid item md={6}>
                                        <RHFTextField name="last_name" label="Last Name" sx={{ mb: 3 }} />
                                    </Grid>
                                </Grid>

                                <RHFTextField name="email" label="Email Address" sx={{ mb: 3 }} />
                                <RHFTextField name="phone" label="Phone Number" sx={{ mb: 3 }} />

                                <RHFSelect name="gender" label="Gender" placeholder="Gender" required >
                                    <option value="" />
                                    {['Female', "Male"].map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </RHFSelect>
                            </Box>
                            <br />
                            <p>Change Referral/promo code</p>
                            <RHFTextField name="referral_id" label="Promo code" sx={{ mb: 3, mt: 2 }} />
                        </Card>
                    </FormProvider>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <LoadingButton type="submit" variant="contained" onClick={handleSubmit(onSubmit)} loading={isSubmitting}>
                        Save Changes
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        

    function dataURLtoFile(dataurl, filename) {
        const arr = dataurl.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);

        // eslint-disable-next-line no-plusplus
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        const croppedImage = new File([u8arr], filename, { type: mime });
        // setState({...state, croppedImage });
        setCropImage(croppedImage);
    }

    function getCroppedImg(image, crop) {
        const canvas = document.createElement("canvas");
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        canvas.width = crop.width;
        canvas.height = crop.height;
        const ctx = canvas.getContext("2d");

        ctx.drawImage(
            image,
            crop.x * scaleX,
            crop.y * scaleY,
            crop.width * scaleX,
            crop.height * scaleY,
            0,
            0,
            crop.width,
            crop.height
        );

        const reader = new FileReader()
        canvas.toBlob(blob => {
            reader.readAsDataURL(blob)
            reader.onloadend = () => {
                dataURLtoFile(reader.result, 'userfile.jpg')
            }
        });
    }

    function onCropComplete(crop) {
        // console.log("state",state);
        if (state.imageRef) {
            getCroppedImg(state.imageRef, crop);
        }
    }
}